import axios from 'axios';
import { ElMessage } from 'element-plus';
import { getToken, clearAuth } from '@/utils/auth';

/**
 * 将 HTTP/网络错误转成统一“响应对象”，避免未捕获的 Promise rejection 让页面报 Uncaught。
 * 注意：业务层 code!=200 仍在上面的 success 分支中 reject（保持现有行为）。
 */
const toErrorResponse = (code, msg) => ({
  code,
  msg,
  data: null,
  rows: [],
  total: 0
});

/**
 * 创建axios实例
 * 参考后端Sa-Token配置：
 * - token-name: Authorization
 * - token-prefix: Bearer
 */
const service = axios.create({
  baseURL: '/lpe', // API基础URL（必须以 / 开头才能匹配代理规则）
  timeout: 60000, // 请求超时时间（60秒）
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
});

/**
 * 请求拦截器 - 自动携带Token
 */
service.interceptors.request.use(
  config => {
    // 从localStorage获取token并添加到请求头
    const token = getToken();
    if (token) {
      // 后端配置：token-name=Authorization, token-prefix=Bearer
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

/**
 * 处理401未授权错误 - 统一处理逻辑
 */
const handle401Error = (message = '登录会话已过期，请重新登录！') => {
  // 判断token是否存在
  const token = getToken();
  if(!token){
    ElMessage.warning("请先进行登录！");
    // 未登录状态，触发登录弹窗
    window.dispatchEvent(new CustomEvent('show-login-dialog'))
  }else{
    // 已登录但token过期或被强制下线
    clearAuth();
    ElMessage.warning(message);
    // 延迟刷新页面到首页，确保清除所有用户信息（包括头像等）
    setTimeout(() => {
      window.location.href = process.env.BASE_URL || '/'
    }, 1500)
  }
};

/**
 * 响应拦截器 - 处理Token过期和错误
 */
service.interceptors.response.use(
  async response => {
    const res = response.data;
    const config = response.config || {};
    const skipGlobalErrorHandler = config.skipGlobalErrorHandler;

    // Blob 下载场景：后端可能返回 JSON 错误，但由于 responseType=blob 被包装成 Blob。
    if (config.responseType === 'blob' && res instanceof Blob && res.type.includes('json')) {
      try {
        const text = await res.text()
        const parsed = JSON.parse(text)
        const message = (parsed && (parsed.msg || parsed.message)) ? String(parsed.msg || parsed.message) : '导出失败'

        if (!skipGlobalErrorHandler) {
          ElMessage.error(message)
        }
        return Promise.reject(new Error(message))
      } catch {
        return res
      }
    }

    // 后端统一返回格式：{ code: 200, msg: '...', data: {...} }
    // 如果code是200，直接返回数据
    if (res.code === 200 || !res.code) {
      return res;
    }

    // 业务错误处理
    const message = res.msg;

    // 如果设置了跳过全局错误处理，直接返回完整响应对象，让调用方自己处理
    if (skipGlobalErrorHandler) {
      return res;
    }

    // 401未授权：业务层返回401（HTTP 200但code 401）
    if (res.code === 401) {
      handle401Error();
      return Promise.reject(new Error(message));
    }

    // 其他业务错误码，显示错误消息
    ElMessage.error(message);
    return Promise.reject(new Error(message));
  },
  error => {
    console.error('响应错误:', error);

    // 获取请求配置，检查是否需要跳过全局错误处理
    const config = error.config || {};
    const skipGlobalErrorHandler = config.skipGlobalErrorHandler;

    // 如果设置了跳过全局错误处理，直接返回完整响应对象（如果有），让调用方自己处理
    if (skipGlobalErrorHandler) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      return Promise.reject(error);
    }

    // 处理HTTP状态码错误
    if (error.response) {
      // responseType=blob 的 HTTP 错误：data 可能是 Blob，尝试将其转为文本解析
      let { status, data } = error.response;
      if (data instanceof Blob && data.type.includes('json')) {
        return data.text().then(text => {
          try {
            data = JSON.parse(text);
          } catch (e) {}
          return handleResponseError(status, data, error.message);
        });
      }
      return handleResponseError(status, data, error.message);
    } else if (error.request) {
      // 断网/服务不可达：提示即可，且不 reject，避免到处 Uncaught
      ElMessage.error('服务不可用或网络异常，请稍后重试');
      return Promise.resolve(toErrorResponse(0, 'network error'));
    } else {
      ElMessage.error('请求配置错误，请检查请求参数');
      return Promise.resolve(toErrorResponse(-1, 'config error'));
    }
  }
);

function handleResponseError(status, data, defaultMessage) {
  const message = (data && (data.msg || data.message)) || defaultMessage;
  switch (status) {
    case 401:
      handle401Error(message);
      break;
    case 403:
      ElMessage.error('没有权限访问该资源');
      break;
    case 500:
    case 502:
      ElMessage.error(message || '服务器异常，请稍后重试');
      break;
    case 404:
      ElMessage.error('请求资源不存在');
      break;
    default:
      console.error(`HTTP错误(${status}):`, message);
      ElMessage.error(message);
  }
  return Promise.resolve(toErrorResponse(status, message));
}

/**
 * 通用请求方法
 * @param {Object} config - 请求配置
 * @returns {Promise} 返回请求结果Promise对象
 */
const request = config => {
  return service(config);
};

/**
 * GET请求
 * @param {String} url - 请求URL
 * @param {Object} params - 请求参数
 * @returns {Promise} 返回请求结果Promise对象
 */
export function get(url, params = {}) {
  return service.get(url, { params });
}

/**
 * POST请求
 * @param {String} url - 请求URL
 * @param {Object} data - 请求数据
 * @returns {Promise} 返回请求结果Promise对象
 */
export function post(url, data = {}) {
  return service.post(url, data);
}

/**
 * PUT请求
 * @param {String} url - 请求URL
 * @param {Object} data - 请求数据
 * @returns {Promise} 返回请求结果Promise对象
 */
export function put(url, data = {}) {
  return service.put(url, data);
}

/**
 * DELETE请求
 * @param {String} url - 请求URL
 * @param {Object} params - 请求参数
 * @returns {Promise} 返回请求结果Promise对象
 */
export function del(url, params = {}) {
  return service.delete(url, { params });
}

export default request;
